import { useContext, useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { ServiceContext } from "../../../contexts/ServiceContext";
import { ReactComponent as AlarmIcon } from "../../../icons/alarm.svg";
import { ReactComponent as DateIcon } from "../../../icons/date.svg";
import { ReactComponent as MenuIcon } from "../../../icons/menu.svg";
import { ReactComponent as EmailsMenuIcon } from "../../../icons/email-menu.svg";
import { ReactComponent as DevicesMenuIcon } from "../../../icons/devices-menu.svg";
import { ReactComponent as UsersMenuIcon } from "../../../icons/users-menu.svg";
import { ReactComponent as CompaniesMenuIcon } from "../../../icons/company-menu.svg";
import { ReactComponent as MessagesMenuIcon } from "../../../icons/messages-menu.svg";
import { ReactComponent as PhoneNumberMenuIcon } from "../../../icons/phone-number-menu.svg";
import Datetime from "react-datetime";
import config from "../../../config";
import "./deviceAlarms.css";
import "react-datetime/css/react-datetime.css";

function DeviceAlarms() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(true);
  const [viewTable, setViewTable] = useState(true);
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLatest, setShowLatest] = useState(true);
  const [toDateDisable, setDateDisable] = useState(true);
  const [availableCompanies, setAvailableCompanies] = useState("");
  const [availableUsers, setAvailableUsers] = useState("");
  const [availableDevices, setAvailableDevices] = useState("");
  const [emailNotifications, setAvailableEmailNotifications] = useState("");
  const [messagesNotifications, setAvailablemMssagesNotifications] = useState("");
  const [phoneCallsNotifications, setAvailablePhoneCallsNotifications] = useState("");
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get("currentPage")) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [hoveredChannel, setHoveredChannel] = useState(null);
  const itemsPerPage = config.uiSettings.itemsPerPage || 20; 
  const channels = ["CH1", "CH2", "CH3", "CH4"];
  const channelIndexMap = {CH1: 1, CH2: 2, CH3: 3, CH4: 4,};
  const colors = { CH1: "#8884d8", CH2: "#82ca9d", CH3: "#ffc658", CH4: "#ff7300" };
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    d.setHours(0, 0, 0, 0); 
    return d;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date(); 
    d.setHours(23, 59, 0, 0); 
    return d;
  });
  const {dataService, infrastructureService} = useContext(ServiceContext);
  const [selectedInterval, setSelectedInterval] = useState({ id: 2, name: "Last week", fromDateTime: new Date(new Date().setDate(new Date().getDate() - 7)) });
    
  const timeIntervals = [
    { id: 1, name: "Custom", fromDateTime: new Date(new Date().setDate(new Date().getDate() - 7)) },
    { id: 2, name: "Last week", fromDateTime: new Date(new Date().setDate(new Date().getDate() - 7)) },
    { id: 3, name: "Last month", fromDateTime: new Date(new Date().setMonth(new Date().getMonth() - 1)) },
    { id: 4, name: "Last 2 months", fromDateTime: new Date(new Date().setMonth(new Date().getMonth() - 2)) },
    { id: 5, name: "Last 3 months", fromDateTime: new Date(new Date().setMonth(new Date().getMonth() - 3)) },
    { id: 6, name: "Last 6 months", fromDateTime: new Date(new Date().setMonth(new Date().getMonth() - 6)) },
    { id: 7, name: "Last year", fromDateTime: new Date(new Date().setFullYear(new Date().getFullYear() - 1)) },
  ];
    
  const { deviceId } = useParams();

  const navigate = useNavigate();   
  
  const handlePageChange = (p) => {
    setCurrentPage(p);
    setSearchParams({currentPage: p});
  }; 

  const handleFromDateChange = (date) => {   
    setCurrentPage(1);    
    setFromDate(date);  
    setSelectedInterval(timeIntervals[0]);
    loadAlarms();   
    setSearchParams({currentPage: 1});
  };

  const handleToDateChange = (date) => { 
    setCurrentPage(1);   
    setToDate(date);  
    setSelectedInterval(timeIntervals[0]);
    loadAlarms();  
    setSearchParams({currentPage: 1});
  };

  const renderLegend = () => (
    <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
      {channels.map(channel => (
        <div
          key={channel}
          onMouseEnter={() => setHoveredChannel(channel)}
          onMouseLeave={() => setHoveredChannel(null)}
          style={{
            color: colors[channel],
            fontWeight: hoveredChannel === channel ? "bold" : "normal",
            opacity: !hoveredChannel || hoveredChannel === channel ? 1 : 0.1,
            cursor: "pointer"
          }} >
          {channel}
        </div>
      ))}
    </div>
  );

  function AlarmsChart({ data }) {
  const [chartHeight, setChartHeight] = useState(window.innerHeight - 300);

  useEffect(() => {
    const handleResize = () => setChartHeight(window.innerHeight - 300);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <ResponsiveContainer width="99%" height={chartHeight}>
      <LineChart margin={{ top: 20, right: 30, left: 60, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          type="number"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(time) =>
            new Date(time).toLocaleDateString() +
            " " +
            new Date(time).toLocaleTimeString()
          }/>
        <YAxis
          dataKey="channel"
          type="number"
          domain={[1, 4]}
          ticks={[1, 2, 3, 4]}
          tickFormatter={(val) =>
            Object.keys(channelIndexMap).find(
              (key) => channelIndexMap[key] === val
            )
          }/>
        <Legend content={renderLegend} />
        <Tooltip content={({ active, payload, label }) => {
          if (active && payload && payload.length) {
            const time = new Date(label).toLocaleString();
            return (
               <div style={{ background: "white", padding: "5px", border: "1px solid #ccc"}}>
                <p>{time}</p>
                {payload.filter(p => p.payload.channel !== 0).map((p, i) => (
                  <p key={i} style={{ color: p.color, margin: 0 }}>
                    {p.payload.channelName}
                  </p>
                ))}
               </div>
            );
          }
          return null;
        }}/>

        {channels.map((channel) => (
          <Line
            key={channel}
            type="monotone"
            data={data.filter((d) => d.channelName === channel)}
            dataKey="channel"
            name={channel}
            stroke={colors[channel]}
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
            opacity={
              !hoveredChannel || hoveredChannel === channel ? 1 : 0.2
            }
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

  const loadAlarms = useCallback(async () => {
    try {
      setLoading(true);
      let getAlarms = {
        deviceId: deviceId,
        startDateTime: fromDate.toISOString().slice(0, 19),
        endDateTime: toDate.toISOString().slice(0, 19),   
        fromIndex: viewTable ? ((currentPage - 1) * itemsPerPage) : 0,
        itemsCount: viewTable ? itemsPerPage : ""
      };
      const data = await dataService.get("DeviceAlarms", getAlarms); 
      const reversedItems = [...data.items].reverse();       
      
      setAlarms(viewTable ? data.items : reversedItems);
      setTotalPages(Math.ceil(data.totalItemsCount / itemsPerPage));
    } catch (error) {
      console.error("Error fetching device alarms:", error);
    } finally {
      setLoading(false);
    }
  }, [dataService, deviceId, fromDate, toDate, viewTable, currentPage, itemsPerPage]);
  
  useEffect(() => {
    loadAlarms();
  }, [dataService, deviceId, loadAlarms]);

  const loadStatisticSummary = useCallback(async () => {
    try {
      setLoading(true);
       const data = await infrastructureService.get("Statistic"); 
       setAvailableCompanies(data.availableCompanies);
       setAvailableDevices(data.availableDevices);
       setAvailableUsers(data.availableUsers);
       setAvailableEmailNotifications(data.emailNotifications);
       setAvailablemMssagesNotifications(data.smsNotifications);
       setAvailablePhoneCallsNotifications(data.phoneCallsNotifications);
    } catch (error) {
       console.error("Error fetching statistic summary:", error);
    } finally {
      setLoading(false);
    }
  }, [infrastructureService]);

  useEffect(() => {
     loadStatisticSummary()
  }, [loadStatisticSummary]);

  function Card({id, dateTimeUtc, alarmChannelName, deviceAlarmChannelName}) {
    
    return (
      <div className="card-border">
        <div className="card-div" style={{ textAlign:"start", paddingBottom:"20px" }}>
          <div className="div-inline-30">
            <label>
              <DateIcon/><span className="span-bold">Date-Time</span><span>: {dateTimeUtc ? new Date(dateTimeUtc + "Z").toLocaleString() : "—"}</span>
            </label>
          </div>
          <div className="div-inline-30">
            <label>
              <AlarmIcon/><span className="span-bold">{alarmChannelName}</span><span>: {deviceAlarmChannelName}</span>
            </label>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const id = Number(e.target.value);
    const interval = timeIntervals.find(t => t.id === id);
    if (interval) {
      setCurrentPage(1);   
      setSelectedInterval(interval);
      setFromDate(interval.fromDateTime);
      loadAlarms();
      setSearchParams({currentPage: 1});
    }
  };  

  const handleShowLatestChange = (e) => {
    setCurrentPage(1);   
    setShowLatest(e.target.checked);
    setDateDisable(e.target.checked);
    setSearchParams({currentPage: 1});
  }

  const chartData = alarms.map(a => ({
    time: new Date(a.dateTimeUtc).getTime(), 
    channel: channelIndexMap[a.alarmChannelName], 
    channelName: a.alarmChannelName             
  }));

  useEffect(() => {
    const newCurrentPage = Number(searchParams.get("currentPage")) || 1;
    setCurrentPage(newCurrentPage);
  }, [searchParams]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 600) {
        setOpen(true);
      } else {
        setOpen(false);
      }
    };
    
    handleResize();
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPages = () => {
    const pages = [];
    const delta = 1; 
  
    for (let i = 1; i <= Math.min(2, totalPages); i++) {
      pages.push(i);
    }
  
    const start = Math.max(currentPage - delta, 3);
    const end = Math.min(currentPage + delta, totalPages - 2);

    if (start > 3) pages.push("left-ellipsis");

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) pages.push("right-ellipsis");

    if (totalPages > 3) {
      for (let i = Math.max(totalPages - 1, end + 1); i <= totalPages; i++) {
        pages.push(i);
      }
    }

    return [...new Set(pages)]; 
  }

  if (loading) return <div>Loading...</div>;

  return ( 
     <div>
        <div className="div-menu">
            <div onClick={() => setOpen(true)} className="menu-button">
                <label><svg width="30" height="30" fill="currentColor" viewBox="0 0 16 16">
                            <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 
                                                         0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"/>
                </svg></label>
            </div>
            {open && (
                <div className="modal">
                    <div className="modal-content">
                        <div className="col-span-12 h-full rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-gray-100 md:col-span-3 lg:col-span-2 modal-content-div">
                        <div className="close-modal" onClick={() => setOpen(false)}>
                            <MenuIcon/>
                       </div>
                       <div className="space-y-6">
                          <div>
                              <p className="px-2 text-sm font-semibold text-gray-600">Overview</p>
                              <div className="mt-2 space-y-1">
                                <Link className="link-item" to="/">
                                   <label className="link-lab"> 
                                      <CompaniesMenuIcon/> Companies
                                      <label className="label-float-end">{availableCompanies}</label>
                                   </label>
                                </Link>
                                <Link className="link-item" to="/users">
                                   <label className="link-lab">
                                       <UsersMenuIcon/> Users
                                       <label className="label-float-end">{availableUsers}</label>
                                   </label>
                                </Link>
                                <Link className="link-item" to="/devices">
                                   <label className="link-lab">
                                      <DevicesMenuIcon/> Devices
                                      <label className="label-float-end">{availableDevices}</label>
                                  </label>
                                </Link>
                              </div>
                              <hr/>
                           </div>
                           
                           <div className="pt-2">
                              <p className="px-2 text-sm font-semibold text-gray-600">Notifications</p>
                              <div className="mt-2 space-y-1">
                                <Link className="link-item" to="/emails">
                                  <label className="link-lab">
                                    <EmailsMenuIcon/> Emails
                                    <label className="label-float-end">{emailNotifications}</label>
                                  </label>
                                 </Link>
                                <Link className="link-item" to="/messages">
                                   <label className="link-lab">
                                      <MessagesMenuIcon/> Messages
                                      <label className="label-float-end">{messagesNotifications}</label>
                                    </label>
                                </Link>
                                <Link className="link-item" to="/phoneCalls">
                                   <label className="link-lab">
                                     <PhoneNumberMenuIcon/> Phone Calls
                                     <label className="label-float-end">{phoneCallsNotifications}</label>
                                   </label>
                                </Link>
                              </div>
                              <hr/>
                           </div>
                           
                           <div className="pt-2">
                              <div>
                                <div style={{  display: "inline-block"}}>
                                    <p className="px-2 text-sm font-semibold text-gray-600">Device Details</p>
                                </div>
                                <div style={{  display: "inline-block", float:"right", fontSize:"20px", cursor: "pointer"}} onClick={() => navigate("/devices")}>
                                   <p className="px-2 text-sm font-semibold text-gray-600"> X </p>
                                </div>
                              </div>
                              <div className="mt-2 space-y-1">
                                <Link className="link-item" to={`/deviceInfo/${deviceId}`}>
                                  <label>
                                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 
                                                 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 
                                                 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0"/>
                                      </svg> General Information
                                  </label>
                                </Link>
                                <Link className="link-item" to={`/statuses/${deviceId}`}>
                                   <label>
                                      <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                         <path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022zm2.004.45a7 7 0 0 0-.985-.299l.219-.976q.576.129 
                                                  1.126.342zm1.37.71a7 7 0 0 0-.439-.27l.493-.87a8 8 0 0 1 .979.654l-.615.789a7 7 0 0 0-.418-.302zm1.834 1.79a7 
                                                  7 0 0 0-.653-.796l.724-.69q.406.429.747.91zm.744 1.352a7 7 0 0 0-.214-.468l.893-.45a8 8 0 0 1 
                                                  .45 1.088l-.95.313a7 7 0 0 0-.179-.483m.53 2.507a7 7 0 0 0-.1-1.025l.985-.17q.1.58.116 1.17zm-.131 
                                                  1.538q.05-.254.081-.51l.993.123a8 8 0 0 1-.23 1.155l-.964-.267q.069-.247.12-.501m-.952 
                                                  2.379q.276-.436.486-.908l.914.405q-.24.54-.555 1.038zm-.964 1.205q.183-.183.35-.378l.758.653a8 8 0 0 1-.401.432z"/>
                                         <path d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0z"/>
                                         <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5"/>
                                      </svg> Status History
                                    </label>
                                </Link>
                                <Link className="link-item-selected" to={`/alarms/${deviceId}`}>
                                   <label>
                                     <svg fill="currentColor" width="20" height="20" viewBox="-50 -150 550 550">
                                        <g id="XMLID_140_">
                                            <g>
                                                <g>
                                                    <path d="M326.39,233.237v-30.34c0-48.263-33.197-88.766-78-99.938V72.124c0-13.807-11.193-25-25-25s-25,11.193-25,25v30.835 
                                                              c-44.803,11.172-78,51.675-78,99.938v30.34c0,32.437-12.886,63.546-35.822,86.483c-10.082,10.082-13.098,25.244-7.642,38.417 
                                                              c5.456,13.172,18.31,21.761,32.568,21.761h62.148c0,28.579,23.168,51.747,51.747,51.747c28.579,0,51.747-23.168,51.747-51.747 
                                                              h62.148c14.258,0,27.112-8.589,32.568-21.761c5.456-13.173,2.44-28.335-7.642-38.417 C339.275,296.783,326.39,265.674,326.39,233.237z"/>
                                                    <path d="M86.146,233.065c7.842-2.671,12.034-11.193,9.362-19.035c-8.021-23.55-6.392-48.815,4.589-71.14 
                                                              c10.98-22.325,29.998-39.037,53.548-47.058c7.842-2.671,12.034-11.193,9.362-19.036c-2.671-7.842-11.193-12.034-19.035-9.362 
                                                              c-31.136,10.605-56.278,32.7-70.795,62.215c-14.517,29.516-16.672,62.918-6.066,94.053 C69.782,231.546,78.306,235.736,86.146,233.065z"/>
			                                        <path d="M130.078,44.416c7.842-2.671,12.034-11.193,9.363-19.035c-2.671-7.842-11.194-12.033-19.036-9.362
                                                              C27.665,47.606-22.086,148.755,9.503,241.496c2.671,7.843,11.195,12.033,19.035,9.362c7.842-2.671,12.034-11.193,9.362-19.035
                                                            c-12.718-37.34-10.134-77.397,7.276-112.794C62.586,83.633,92.738,57.134,130.078,44.416z"/>
			                                        <path d="M302.806,67.434c-7.844-2.673-16.364,1.521-19.035,9.362c-2.671,7.842,1.521,16.364,9.362,19.036
				                                              c48.616,16.559,74.696,69.582,58.137,118.198c-2.671,7.842,1.52,16.364,9.362,19.035c7.836,2.671,16.363-1.517,19.035-9.362
				                                             C401.561,159.428,367.081,89.326,302.806,67.434z"/>
			                                       <path d="M428.523,105.788c-20.947-42.587-57.225-74.468-102.149-89.77c-7.844-2.674-16.364,1.521-19.035,9.362
				                                            c-2.671,7.842,1.521,16.364,9.362,19.035c37.34,12.719,67.492,39.217,84.902,74.613c17.41,35.397,19.994,75.454,7.275,112.794
				                                            c-2.671,7.842,1.521,16.364,9.362,19.035c7.836,2.671,16.363-1.517,19.035-9.362C452.578,196.57,449.47,148.375,428.523,105.788z"/>
		                                        </g>
	                                        </g>
                                       </g>
                                   </svg> Alarm History  
                                   </label>
                                </Link>
                              </div>
                              <hr/>
                           </div>
                           <div className="mx-2 h-px bg-gray-200"/>
                        </div>
                </div>
             </div>
          </div>
        )}
        </div>
        <div className='devices'>
                  <div className='filter-st'>                   
                    <div className='div-input-checkbox' >
                      <div className="checkbox-c">
                          <label class="toggle-switch">
                             <input class="toggle-input" id="toggle" type="checkbox" onChange={(e) => setViewTable(e.target.checked)} checked={viewTable}/>
                             <label class="toggle-label" htmlFor="toggle"></label>
                             <span className="toggle-label-text">  {viewTable ? "View table" : "View chart"}</span>
                          </label> 
                      </div>
                    </div>
                     <div className='div-input-datetime-s'>
                      <select style={{ width: "99%" }} value={selectedInterval.id} onChange={handleChange}>
                        {timeIntervals.map(interval => (
                          <option key={interval.id} value={interval.id}>
                            {interval.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='div-input-lab-from'>From:</div>
                    <div className='div-input-datetime'>
                      <Datetime value={fromDate} onChange={handleFromDateChange}  timeFormat={false} />
                    </div>
                    <div className='div-input-lab-to'>To:</div>
                    <div className='div-input-datetime'>
                      <Datetime value={toDate} onChange={handleToDateChange}  timeFormat={false} inputProps={{ disabled: toDateDisable }}/>
                    </div>
                    <div className='div-input-checkbox' >
                      <div className="checkbox-c">
                          <label class="toggle-switch">
                             <input class="toggle-input" id="toggle1" type="checkbox" onChange={handleShowLatestChange} checked={showLatest}/>
                             <label class="toggle-label" htmlFor="toggle1"></label>
                             <span className="toggle-label-text"> Show latest</span>
                          </label> 
                      </div>
                    </div>
                  </div>
          
          {viewTable && (
            <>              
               <div className="pagination-div pagination-div-margin">
                <nav aria-label="pagination">
                   <ul className="pagination">
                    <li>
                      <button disabled={currentPage === 1} onClick={() => handlePageChange(1)}
                          style={{padding: "5px 10px", borderRadius: "15px", border: "1px solid #ccc",
                                  color: "#333", fontWeight: "normal", backgroundColor: currentPage === 1 ? "#e6e6e6ff" : "white" }}>
                         &laquo;
                      </button>
                    </li>
                    {getPages().map((page, index) => page === "left-ellipsis" || page === "right-ellipsis" ? (
                        <li key={index}>…</li>) : (<li key={index}>
                          <button onClick={() => handlePageChange(page)}
                            style={{ margin: "0", padding: "5px 10px", borderRadius: "15px", border: "1px solid #ccc",
                                     backgroundColor: currentPage === page ? "#333" : "white", color: currentPage === page ? "white" : "#333", 
                                     fontWeight: currentPage === page ? "bold" : "normal", cursor: "pointer"}}>
                            {page}
                          </button>
                        </li>
                     ))}
                     <li>
                      <button disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)}
                           style={{padding: "5px 10px", borderRadius: "15px", border: "1px solid #ccc",
                                   color: "#333", fontWeight: "normal", backgroundColor: currentPage === totalPages ? "#e6e6e6ff" : "white" }}>
                        &raquo;
                      </button>
                    </li>
                   </ul>
                </nav>
               </div>
               
                <div className="card-list" style={{minHeight: "400px"}}>
                  {alarms != null && alarms.length > 0 ? (alarms.map((alarm) => (
                    <Card key={alarm.id} id={alarm.id} dateTimeUtc={alarm.dateTimeUtc} alarmChannelName={alarm.alarmChannelName} deviceAlarmChannelName={alarm.deviceAlarmChannelName}/>))) : (
                    <p>No alarms available.</p>
                  )}
                </div>
            </>
          )}
          {!viewTable && 
             <div className="card-list" style={{minHeight: "400px"}} >
                <AlarmsChart data={chartData} />
             </div>
          }
        </div>
     </div>
  )
}

export default DeviceAlarms;